import { useTranslation } from "react-i18next";
import { btnSecondaryCls } from "components/tw/classes";
import StatusChip, { ChipTone } from "components/tw/status-chip";
import { Translate } from "features/cabinet-tasks/task-row";
import { BusinessTrip } from "types/BusinessTrip";

/**
 * What the «Kitas žingsnis» column says for each `nextStep` the server gives a trip.
 * `textKey` is the text. `chipTone` shows it as a coloured label instead of plain text
 * (the two finished kinds). `buttonKey` is the button to the trip page; only a step where
 * the person acts has one. A `nextStep` not listed here (an older server sends none)
 * adds nothing to the row.
 */
const TRIP_NEXT_STEPS: Record<string, { textKey: string; chipTone?: ChipTone; buttonKey?: string }> = {
  create_order: { textKey: "cabinet_trip_step_create_order", buttonKey: "cabinet_action_continue" },
  sign_order: { textKey: "cabinet_trip_step_sign_order", buttonKey: "tasks_action_sign" },
  wait_order: { textKey: "cabinet_trip_step_wait_order" },
  trip_ahead: { textKey: "cabinet_trip_step_trip_ahead" },
  fill_report: { textKey: "cabinet_trip_step_fill_report", buttonKey: "tasks_action_fill_report" },
  sign_report: { textKey: "cabinet_trip_step_sign_report", buttonKey: "tasks_action_sign" },
  wait_report: { textKey: "cabinet_trip_step_wait_report" },
  fix_report: { textKey: "cabinet_trip_step_fix_report", buttonKey: "cabinet_action_fix" },
  done: { textKey: "cabinet_trip_step_done", chipTone: "green" },
  cancelled: { textKey: "cabinet_trip_step_cancelled", chipTone: "grey" },
};

/**
 * What the line over the list counts. How it is read: the first group in which any trip
 * waits is the line (its own text for exactly one trip, the counted text for more); when no
 * trip waits in either, all is in order.
 */
const WAITING_GROUPS = [
  {
    steps: ["fill_report", "fix_report"],
    oneKey: "cabinet_trips_waiting_report_single",
    manyKey: "cabinet_trips_waiting_report",
  },
  {
    steps: ["sign_order", "sign_report"],
    oneKey: "cabinet_trips_waiting_signature_single",
    manyKey: "cabinet_trips_waiting_signature",
  },
];

/** The line over the list, and whether it asks the person to do something. */
export function tripsStatusLine(trips: BusinessTrip[], t: Translate) {
  for (const { steps, oneKey, manyKey } of WAITING_GROUPS) {
    const count = trips.filter((trip) => trip.nextStep && steps.includes(trip.nextStep)).length;
    if (count > 0) return { text: count === 1 ? t(oneKey) : t(manyKey, { count }), needsAction: true };
  }
  return { text: t("cabinet_trips_all_in_order"), needsAction: false };
}

/** The cell of a trip in the «Kitas žingsnis» column. */
export function TripNextStepText({ step }: { step?: string }) {
  const { t } = useTranslation();
  const known = step ? TRIP_NEXT_STEPS[step] : undefined;
  if (!known) return null;
  return known.chipTone ? <StatusChip tone={known.chipTone}>{t(known.textKey)}</StatusChip> : <>{t(known.textKey)}</>;
}

/** The button of a trip whose next step is the person's own; it opens the trip page. It sits above the card's stretched link. */
export function TripNextStepButton({ step, onOpen }: { step?: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const buttonKey = step ? TRIP_NEXT_STEPS[step]?.buttonKey : undefined;
  if (!buttonKey) return null;
  return (
    <button type="button" className={`${btnSecondaryCls} relative z-10`} onClick={onOpen}>
      {t(buttonKey)}
    </button>
  );
}
